import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Search, Trash2, ExternalLink } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import InitialsAvatar from '../InitialsAvatar';
import StickersRail from './StickersRail';
import { useBrandPitches } from '../../context/BrandPitchesContext';
import { usePitches, PITCH_STATUSES } from '../../context/PitchesContext';

const fmtGBP = (v) => `£${Math.round(Number(v) || 0).toLocaleString()}`;

const CheckCell = ({ outbound, stickers, hasPP, hasEngaged, brandId, talentId, onChanged, customStickers, onCreateSticker, onDeleteSticker }) => {
  const [openId, setOpenId] = useState(null); // id of the pitch whose popover is open

  const add = async (e) => {
    e.stopPropagation();
    try {
      await api.post('/pitching/increment', { brand_id: brandId, talent_id: talentId });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'save failed'); }
  };
  const toggleSticker = async (sticker) => {
    try {
      await api.post('/pitching/toggle-sticker', { brand_id: brandId, talent_id: talentId, sticker });
      onChanged();
    } catch { toast.error('failed to save sticker'); }
  };

  return (
    <div className="relative flex flex-col items-center justify-center gap-1 group">
      <StickersRail
        hasPP={hasPP}
        hasEngaged={hasEngaged}
        manualStickers={stickers}
        canEdit
        onToggle={toggleSticker}
        customStickers={customStickers}
        onCreateSticker={onCreateSticker}
        onDeleteSticker={onDeleteSticker}
      />
      <div className="flex flex-wrap items-center justify-center gap-1">
        {outbound.map((p, i) => (
          <PitchChip
            key={p.id}
            pitch={p}
            index={i + 1}
            open={openId === p.id}
            onOpen={() => setOpenId(p.id)}
            onClose={() => setOpenId(null)}
            onSaved={onChanged}
          />
        ))}
        <button
          onClick={add}
          className="h-6 min-w-[26px] px-1.5 rounded-md border border-dashed border-slate-300 text-slate-400 text-xs font-semibold inline-flex items-center justify-center hover:border-pink-400 hover:text-pink-600 hover:bg-pink-50 transition-colors"
          title="add a new pitch"
          data-testid={`pitch-plus-${brandId}-${talentId}`}
        >+</button>
      </div>
    </div>
  );
};

const PitchChip = ({ pitch, index, open, onOpen, onClose, onSaved }) => {
  const { updatePitch, removePitch } = usePitches();
  const [title, setTitle] = useState(pitch.title || '');
  const [notes, setNotes] = useState(pitch.notes || '');
  const [status, setStatus] = useState(pitch.status || 'pitched');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(pitch.title || '');
    setNotes(pitch.notes || '');
    setStatus(pitch.status || 'pitched');
  }, [pitch.id, pitch.title, pitch.notes, pitch.status, open]);

  const save = async () => {
    setSaving(true);
    try {
      await updatePitch(pitch.id, { title: title.trim() || pitch.title, notes, status });
      onSaved?.();
      onClose();
    } catch { toast.error('save failed'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('delete this pitch?')) return;
    setSaving(true);
    try {
      await removePitch(pitch.id);
      onSaved?.();
      onClose();
    } catch { toast.error('delete failed'); }
    finally { setSaving(false); }
  };

  const statusMeta = PITCH_STATUSES.find((s) => s.id === status) || PITCH_STATUSES[0];

  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        className={`h-6 min-w-[26px] px-1.5 rounded-md text-xs font-semibold inline-flex items-center justify-center gap-1 transition-colors border ${pitch.notes ? 'ring-1 ring-pink-200' : ''} bg-pink-500 border-pink-500 text-white hover:bg-pink-600`}
        title={`pitch #${index}${pitch.notes ? ' · has notes' : ''}`}
        data-testid={`pitch-chip-${pitch.id}`}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusMeta.color }} />
        {index}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={onClose} />
          <div className="absolute z-40 top-full mt-1 left-1/2 -translate-x-1/2 w-72 bg-white rounded-lg shadow-xl border border-slate-200 p-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">pitch #{index}</div>
              <button onClick={remove} className="text-slate-400 hover:text-red-600 p-1 rounded" title="delete this pitch">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-[11px] text-slate-500 mb-1">pitch name</div>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. AW campaign · gifting round 1"
              className="h-8 text-xs mb-2"
              data-testid={`pitch-title-input-${pitch.id}`}
            />
            <div className="text-[11px] text-slate-500 mb-1">status</div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 text-xs mb-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PITCH_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mb-1">notes</div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="text-xs resize-none"
              placeholder="what you sent, follow-up plan, anything to remember…"
            />
            <div className="mt-2 flex items-center justify-end gap-1">
              <button onClick={onClose} className="text-[11px] text-slate-500 hover:text-slate-800 px-2 py-1">cancel</button>
              <button onClick={save} disabled={saving} className="text-[11px] font-semibold text-white bg-pink-500 hover:bg-pink-600 rounded px-2 py-1 disabled:opacity-50">
                {saving ? 'saving…' : 'save'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const monthsAgo = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (months <= 0) return 'this month';
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? '1 year ago' : `${years} years ago`;
};

const BrandRow = ({ brand, talents, rank, onCellChanged, onSaveMasterNotes, showStaleBadge, customStickers, onCreateSticker, onDeleteSticker }) => {
  const [editingNotes, setEditingNotes] = useState(false);
  const [draft, setDraft] = useState(brand.pitching_notes || '');
  useEffect(() => { setDraft(brand.pitching_notes || ''); }, [brand.pitching_notes]);

  return (
    <tr className="hover:bg-pink-50/30 group border-b border-pink-100">
      <td className="px-3 py-2 sticky z-10 bg-white group-hover:bg-pink-50/30 text-center" style={{ left: 0, minWidth: 140 }}>
        <span
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-semibold max-w-full"
          style={{ background: '#fce7f3', color: '#9d174d', border: '1px solid #f9a8c8' }}
          title={brand.name}
        >
          {brand.logo && <span className="text-sm leading-none">{brand.logo}</span>}
          <span className="truncate max-w-[100px]">{brand.name}</span>
        </span>
        {showStaleBadge && brand.last_deal_date && (
          <div className="text-[10px] text-amber-600 mt-1">last worked · {monthsAgo(brand.last_deal_date)}</div>
        )}
      </td>
      <td className="px-2 py-2 sticky z-10 bg-white group-hover:bg-pink-50/30 text-center" style={{ left: 140, minWidth: 70 }}>
        {brand.deal_value_total > 0 ? (
          <span
            className="inline-flex items-center h-7 px-3 rounded-full text-[12px] font-semibold tabular-nums"
            style={{ background: '#f3ecfb', color: '#5b21b6', border: '1px solid #d3c1e1' }}
          >{fmtGBP(brand.deal_value_total)}</span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>
      <td className="px-3 py-2 sticky z-10 bg-white group-hover:bg-pink-50/30 text-center" style={{ left: 210, minWidth: 120 }} onClick={(e) => e.stopPropagation()}>
        {editingNotes ? (
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { setEditingNotes(false); if (draft !== (brand.pitching_notes || '')) onSaveMasterNotes(draft); }}
            onKeyDown={(e) => { if (e.key === 'Escape') { setEditingNotes(false); setDraft(brand.pitching_notes || ''); } }}
            rows={2}
            className="w-full text-xs text-center border border-pink-300 rounded p-1.5 outline-none focus:ring-2 focus:ring-pink-200 resize-none"
            data-testid={`master-notes-${brand.id}`}
          />
        ) : (
          <button
            onClick={() => setEditingNotes(true)}
            className="text-center w-full text-xs text-slate-600 hover:text-pink-600 whitespace-pre-wrap truncate"
            title={brand.pitching_notes || 'click to add a relationship note'}
          >
            {brand.pitching_notes || <span className="text-slate-300 italic">+ relationship note</span>}
          </button>
        )}
      </td>
      {talents.map((t) => {
        const cell = brand.per_talent.find((x) => x.talent_id === t.user_id);
        return (
          <td key={t.user_id} className="px-0.5 py-2 text-center align-middle" style={{ minWidth: 60 }}>
            <CheckCell
              outbound={cell?.outbound || []}
              stickers={cell?.stickers || []}
              hasPP={Boolean(cell?.has_pp)}
              hasEngaged={Boolean(cell?.has_engaged)}
              brandId={brand.id}
              talentId={t.user_id}
              onChanged={onCellChanged}
              customStickers={customStickers}
              onCreateSticker={onCreateSticker}
              onDeleteSticker={onDeleteSticker}
            />
          </td>
        );
      })}
    </tr>
  );
};

const MasterPitchingGrid = () => {
  const [data, setData] = useState({ talents: [], brands: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'with_deals' | 'without_deals'
  const [sortMode, setSortMode] = useState('value'); // 'value' | 'alpha'
  const [customStickers, setCustomStickers] = useState([]);
  const { refresh: refreshBrandPitches } = useBrandPitches();
  const { refresh: refreshOutboundPitches } = usePitches();

  const load = async () => {
    try {
      const { data } = await api.get('/pitching/master');
      setData(data);
    } catch { toast.error('failed to load pitching grid'); }
  };

  useEffect(() => {
    (async () => { setLoading(true); await load(); setLoading(false); })();
    api.get('/pitching/stickers').then(({ data }) => setCustomStickers(data || [])).catch(() => {});
  }, []);

  const onCellChanged = async () => {
    await load();
    refreshBrandPitches?.();
    refreshOutboundPitches?.();
  };

  const createSticker = async (def) => {
    try {
      const { data } = await api.post('/pitching/stickers', def);
      setCustomStickers((prev) => [...prev, data]);
      return data;
    } catch { return null; }
  };
  const deleteSticker = async (id) => {
    try {
      await api.delete(`/pitching/stickers/${id}`);
      setCustomStickers((prev) => prev.filter((s) => s.id !== id));
      await load();
    } catch { /* silent */ }
  };

  const saveMasterNotes = async (brandId, notes) => {
    try {
      await api.patch(`/brands/${brandId}`, { pitching_notes: notes });
      setData((d) => ({ ...d, brands: d.brands.map((b) => b.id === brandId ? { ...b, pitching_notes: notes } : b) }));
    } catch { toast.error('failed to save master notes'); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const now = new Date();
    const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    const rows = data.brands.filter((b) => {
      const hasDeals = (b.deal_value_total || 0) > 0;
      if (filterMode === 'with_deals' && !hasDeals) return false;
      if (filterMode === 'without_deals' && hasDeals) return false;
      if (filterMode === 'stale') {
        if (!hasDeals) return false;
        const last = b.last_deal_date ? new Date(b.last_deal_date) : null;
        if (!last || isNaN(last.getTime())) return false;
        if (last >= threeMonthsAgo) return false;
      }
      if (!s) return true;
      return (b.name || '').toLowerCase().includes(s) || (b.pitching_notes || '').toLowerCase().includes(s);
    });
    if (sortMode === 'alpha') {
      return [...rows].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
    }
    if (filterMode === 'stale') {
      // oldest last-activity first (most in need of re-pitching)
      return [...rows].sort((a, b) => (a.last_deal_date || '').localeCompare(b.last_deal_date || ''));
    }
    // 'value' — already sorted by deal value from the API, keep that order
    return rows;
  }, [data.brands, q, filterMode, sortMode]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="find a brand…" className="pl-9 h-9 w-64" />
        </div>

        <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5" data-testid="grid-filter-tabs">
          {[
            { id: 'all', label: 'all brands' },
            { id: 'with_deals', label: 'with previous deals' },
            { id: 'without_deals', label: 'without previous deals' },
            { id: 'stale', label: 'to retain' },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setFilterMode(opt.id)}
              className={`px-3 h-8 text-[11px] font-semibold rounded-full transition ${filterMode === opt.id ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`grid-filter-${opt.id}`}
            >{opt.label}</button>
          ))}
        </div>

        <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5 ml-1" data-testid="grid-sort-tabs">
          {[
            { id: 'value', label: 'by £ value' },
            { id: 'alpha', label: 'A–Z' },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSortMode(opt.id)}
              className={`px-3 h-8 text-[11px] font-semibold rounded-full transition ${sortMode === opt.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid={`grid-sort-${opt.id}`}
            >{opt.label}</button>
          ))}
        </div>

        <span className="text-[11px] text-slate-400 ml-auto">click + to add a pitch · click a numbered chip to edit its notes</span>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-400">loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="master-pitching-grid">
          {/* Dusty pink header strip - matches the talent view accordion aesthetic */}
          <div className="flex items-center justify-between gap-4 px-4 py-3" style={{ background: '#d394ab' }}>
            <div className="min-w-0 flex items-baseline gap-2">
              <span className="font-semibold text-white truncate">brand board</span>
              <span className="text-xs text-white/85">· {filtered.length} brand{filtered.length === 1 ? '' : 's'} · {data.talents.length} talent{data.talents.length === 1 ? '' : 's'}</span>
            </div>
            <div className="text-right shrink-0 inline-flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-widest text-white/70">total</span>
              <span className="text-sm font-semibold text-white tabular-nums">{fmtGBP(filtered.reduce((s, b) => s + (Number(b.deal_value_total) || 0), 0))}</span>
            </div>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: '75vh' }}>
            <table className="text-sm border-collapse w-full" style={{ minWidth: 430 + data.talents.length * 60 }}>
              <thead className="bg-slate-50 sticky top-0 z-20">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="text-center font-semibold px-3 py-3 sticky z-30 bg-slate-50" style={{ left: 0, minWidth: 140 }}>brand</th>
                  <th className="text-center font-semibold px-2 py-3 sticky z-30 bg-slate-50" style={{ left: 140, minWidth: 70 }}>£ deal value</th>
                  <th className="text-center font-semibold px-3 py-3 sticky z-30 bg-slate-50" style={{ left: 210, minWidth: 120 }}>relationship</th>
                  {data.talents.map((t) => (
                    <th key={t.user_id} className="px-0 py-3 text-center font-semibold" style={{ minWidth: 60 }}>
                      <span className="text-[11px] normal-case font-semibold text-slate-700 max-w-[60px] truncate inline-block" title={t.name}>{(t.name || '').split(' ')[0]}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, i) => (
                  <BrandRow
                    key={b.id}
                    brand={b}
                    talents={data.talents}
                    rank={i + 1}
                    onCellChanged={onCellChanged}
                    onSaveMasterNotes={(n) => saveMasterNotes(b.id, n)}
                    showStaleBadge={filterMode === 'stale'}
                    customStickers={customStickers}
                    onCreateSticker={createSticker}
                    onDeleteSticker={deleteSticker}
                  />
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={3 + data.talents.length} className="text-center text-slate-400 py-12 text-sm">no brands match</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterPitchingGrid;
